/* ============================================================================
   atom-product-zoom.js — ATOM LED PDP image lightbox
   ----------------------------------------------------------------------------
   Fixes the #1 dead-click: tapping the product image opens a full-screen
   viewer. Pinch + double-tap to zoom, drag to pan, swipe/arrows/keyboard
   between images, +/-/0 (or on-screen buttons) to zoom, close via ✕ / backdrop
   / Esc.

   Dependency-free. Native <dialog> for modal semantics (focus trap, top-layer,
   Esc). Pointer Events for gestures. Safe DOM (no innerHTML). Image list read
   from the existing .gallery-thumb rail. Progressive enhancement: no <dialog>
   support or no images → no-op, gallery behaves exactly as before.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.getElementById('apdp-root');
  if (!root) return;
  if (typeof HTMLDialogElement === 'undefined' || !HTMLDialogElement.prototype.showModal) return; // hard no-op if no real modal dialog

  var SVGNS = 'http://www.w3.org/2000/svg';

  function el(tag, attrs, kids) {
    var n = document.createElement(tag);
    if (attrs) for (var k in attrs) {
      if (k === 'class') n.className = attrs[k];
      else if (k === 'text') n.textContent = attrs[k];
      else n.setAttribute(k, attrs[k]);
    }
    if (kids) for (var i = 0; i < kids.length; i++) if (kids[i]) n.appendChild(kids[i]);
    return n;
  }
  function svgIcon(paths, size, sw) {
    var s = document.createElementNS(SVGNS, 'svg');
    s.setAttribute('viewBox', '0 0 24 24');
    s.setAttribute('width', size); s.setAttribute('height', size);
    s.setAttribute('fill', 'none'); s.setAttribute('stroke', 'currentColor');
    s.setAttribute('stroke-width', sw || 2);
    s.setAttribute('stroke-linecap', 'round'); s.setAttribute('stroke-linejoin', 'round');
    s.setAttribute('aria-hidden', 'true');
    for (var i = 0; i < paths.length; i++) {
      var p = document.createElementNS(SVGNS, 'path');
      p.setAttribute('d', paths[i]); s.appendChild(p);
    }
    return s;
  }

  /* ── Image list (ordered, full-res) from the thumbnail rail ──────────── */
  var thumbs = root.querySelectorAll('.gallery-thumb');
  var images = Array.prototype.map.call(thumbs, function (t) {
    var im = t.querySelector('img');
    return {
      src: t.getAttribute('data-src') || (im ? im.currentSrc || im.src : ''),
      zoomSrc: t.getAttribute('data-zoom-src') || '',
      alt: t.getAttribute('data-alt') || (im ? im.alt : '') || ''
    };
  });
  if (!images.length) return;

  // Request a crisp ~2048px render for the enlarged view (Shopify never upscales
  // beyond the native image), so desktop/retina don't see an upscaled 1400w cap.
  function bigSrc(src) { return src ? src.replace(/([?&]width=)\d+/, '$1' + 2048) : src; }
  function zoomSource(i) {
    var im = images[(i % images.length + images.length) % images.length];
    return im.zoomSrc || bigSrc(im.src);
  }

  // Keep the exact full-size requests alive. Only an image trigger or an open
  // viewer starts these requests; the untouched product page downloads none.
  var warmedZoomImages = new Map();
  function warmZoomImage(i, priority) {
    var src = zoomSource(i);
    if (!src) return;
    var cached = warmedZoomImages.get(src);
    if (cached) {
      if (priority === 'high') cached.fetchPriority = 'high';
      return;
    }
    var preload = new Image();
    preload.decoding = 'async';
    preload.fetchPriority = priority || 'low';
    preload.onload = function () {
      // Decode ahead of navigation without ever changing the displayed image.
      if (preload.decode) preload.decode().catch(function () {});
    };
    preload.onerror = function () {
      // A later hover/open can retry a failed request; never retry in a loop.
      if (warmedZoomImages.get(src) === preload) warmedZoomImages.delete(src);
    };
    warmedZoomImages.set(src, preload);
    preload.src = src;
  }

  /* ── Build the dialog once (safe DOM, no innerHTML) ──────────────────── */
  var dlg = document.createElement('dialog');
  dlg.className = 'apdp-zoom';
  dlg.setAttribute('aria-label', 'Product image viewer');

  var countEl  = el('span', { class: 'apdp-zoom__count', 'data-zoom-count': '', 'aria-live': 'polite' });
  var statusEl = el('span', { class: 'apdp-zoom__sr', 'aria-live': 'polite' }); // SR zoom announcements
  var zoomOut  = el('button', { type: 'button', class: 'apdp-zoom__btn', 'aria-label': 'Zoom out' }, [svgIcon(['M6 12h12'], 20, 2.4)]);
  var zoomIn   = el('button', { type: 'button', class: 'apdp-zoom__btn', 'aria-label': 'Zoom in' },  [svgIcon(['M12 6v12', 'M6 12h12'], 20, 2.4)]);
  var closeBtn = el('button', { type: 'button', class: 'apdp-zoom__btn apdp-zoom__close', 'data-zoom-close': '', 'aria-label': 'Close image viewer' }, [svgIcon(['M6 6l12 12', 'M18 6L6 18'], 22)]);
  var tools = el('div', { class: 'apdp-zoom__tools' }, [zoomOut, zoomIn, closeBtn]);
  var bar = el('div', { class: 'apdp-zoom__bar' }, [countEl, tools]);

  var prevBtn = el('button', { type: 'button', class: 'apdp-zoom__btn apdp-zoom__nav apdp-zoom__prev', 'data-zoom-prev': '', 'aria-label': 'Previous image' }, [svgIcon(['M15 18l-6-6 6-6'], 26, 2.2)]);
  var nextBtn = el('button', { type: 'button', class: 'apdp-zoom__btn apdp-zoom__nav apdp-zoom__next', 'data-zoom-next': '', 'aria-label': 'Next image' }, [svgIcon(['M9 18l6-6-6-6'], 26, 2.2)]);
  var img = el('img', { class: 'apdp-zoom__img', 'data-zoom-img': '', alt: '', draggable: 'false' });
  var stage = el('div', { class: 'apdp-zoom__stage', 'data-zoom-stage': '' }, [img]);
  var hint = el('p', { class: 'apdp-zoom__hint', 'data-zoom-hint': '', text: 'Pinch or double-tap to zoom' });

  dlg.appendChild(bar); dlg.appendChild(statusEl); dlg.appendChild(prevBtn); dlg.appendChild(stage); dlg.appendChild(nextBtn); dlg.appendChild(hint);
  root.appendChild(dlg);

  var index = 0, scale = 1, tx = 0, ty = 0, lastFocus = null;
  var MIN = 1, MAX = 4, STEP = 1.6;
  var clamp = function (v, lo, hi) { return Math.min(hi, Math.max(lo, v)); };

  function applyTransform() {
    img.style.transform = 'translate(' + tx + 'px,' + ty + 'px) scale(' + scale + ')';
    stage.classList.toggle('is-zoomed', scale > 1.01);
  }
  function clampPan() {
    var maxX = Math.max(0, (img.offsetWidth  * scale - stage.clientWidth)  / 2);
    var maxY = Math.max(0, (img.offsetHeight * scale - stage.clientHeight) / 2);
    tx = clamp(tx, -maxX, maxX);
    ty = clamp(ty, -maxY, maxY);
  }
  function reset() { scale = 1; tx = 0; ty = 0; applyTransform(); }
  function announce() {
    statusEl.textContent = scale > 1.01 ? ('Zoomed in ' + (Math.round(scale * 10) / 10) + '×') : 'Actual size';
  }

  function warmZoomNeighbours() {
    if (!dlg.open || images.length < 2) return;
    warmZoomImage(index + 1, 'low');
    warmZoomImage(index - 1, 'low');
  }
  function warmReadyZoomNeighbours() {
    if (img.complete && img.naturalWidth && img.getAttribute('src') === zoomSource(index)) {
      warmZoomNeighbours();
    }
  }
  img.addEventListener('load', warmReadyZoomNeighbours);

  function show(i) {
    index = (i % images.length + images.length) % images.length;
    var im = images[index];
    reset();
    img.alt = im.alt;
    img.removeAttribute('srcset'); img.removeAttribute('sizes');
    img.decoding = 'async';
    img.fetchPriority = 'high';
    warmZoomImage(index, 'high');
    img.src = zoomSource(index);
    countEl.textContent = (index + 1) + ' / ' + images.length;
    dlg.setAttribute('aria-label', (im.alt || 'Product image') + ' (image ' + (index + 1) + ' of ' + images.length + ')');
    var multi = images.length > 1;
    prevBtn.hidden = !multi; nextBtn.hidden = !multi;
  }
  function next() { show(index + 1); }
  function prev() { show(index - 1); }

  function open(i) {
    lastFocus = document.activeElement;
    show(i);
    dlg.showModal();
    warmReadyZoomNeighbours();
    document.body.classList.add('overflow-hidden');          // theme scroll-lock convention (race-free)
    hint.classList.add('is-visible');
    clearTimeout(open._t);
    open._t = setTimeout(function () { hint.classList.remove('is-visible'); }, 1800);
    closeBtn.focus();
  }
  function close() { dlg.close(); }

  dlg.addEventListener('close', function () {              // fires for ✕, Esc, backdrop
    document.body.classList.remove('overflow-hidden');
    reset();                                               // clean state machine on every re-open
    pointers.clear(); startDist = 0; moved = 0; lastTap = 0;
    stage.classList.remove('is-zoomed');
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  });

  /* ── Controls ────────────────────────────────────────────────────────── */
  closeBtn.addEventListener('click', close);
  [{ button: nextBtn, direction: 1 }, { button: prevBtn, direction: -1 }].forEach(function (nav) {
    ['pointerenter', 'focus', 'pointerdown'].forEach(function (eventName) {
      nav.button.addEventListener(eventName, function () {
        if (dlg.open) warmZoomImage(index + nav.direction, 'high');
      }, { passive: true });
    });
  });
  nextBtn.addEventListener('click', function (e) { e.stopPropagation(); next(); });
  prevBtn.addEventListener('click', function (e) { e.stopPropagation(); prev(); });
  zoomIn.addEventListener('click', function (e) { e.stopPropagation(); zoomBy(STEP); });
  zoomOut.addEventListener('click', function (e) { e.stopPropagation(); zoomBy(1 / STEP); });
  dlg.addEventListener('keydown', function (e) {
    if (e.key === 'ArrowRight') { e.preventDefault(); next(); }
    else if (e.key === 'ArrowLeft') { e.preventDefault(); prev(); }
    else if (e.key === '+' || e.key === '=') { e.preventDefault(); zoomBy(STEP); }
    else if (e.key === '-' || e.key === '_') { e.preventDefault(); zoomBy(1 / STEP); }
    else if (e.key === '0') { e.preventDefault(); reset(); announce(); }
  });

  // Keyboard / button zoom: scale around centre, keep the current pan proportional.
  function zoomBy(factor) {
    var prevScale = scale;
    scale = clamp(scale * factor, MIN, MAX);
    if (scale <= 1.01) { tx = 0; ty = 0; }
    else { tx *= scale / prevScale; ty *= scale / prevScale; }
    clampPan(); applyTransform(); announce();
  }

  /* ── Gestures: pinch-zoom · drag-pan · double-tap-zoom · swipe-nav ───── */
  var pointers = new Map();
  var startDist = 0, startScale = 1, panBaseX = 0, panBaseY = 0;
  var downX = 0, moved = 0, downTarget = null, downTime = 0, lastTap = 0;
  var two = function () { return Array.from(pointers.values()); };
  var distance = function (a, b) { return Math.hypot(a.x - b.x, a.y - b.y); };

  function seedPan(p) { panBaseX = p.x - tx; panBaseY = p.y - ty; downX = p.x; moved = 0; }
  function seedPinch() { var p = two(); if (p.length >= 2) { startDist = distance(p[0], p[1]); startScale = scale; } }

  stage.addEventListener('pointerdown', function (e) {
    warmZoomNeighbours();
    try { stage.setPointerCapture(e.pointerId); } catch (_) {} // iOS can throw if pointer already gone
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    downTarget = e.target; downTime = Date.now();
    if (pointers.size >= 2) seedPinch();                 // re-anchor whenever we (re)enter multi-touch
    else seedPan({ x: e.clientX, y: e.clientY });
  });

  stage.addEventListener('pointermove', function (e) {
    if (!pointers.has(e.pointerId)) return;
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.size >= 2 && startDist > 0) {
      var p = two();
      scale = clamp(startScale * (distance(p[0], p[1]) / startDist), MIN, MAX);
      clampPan(); applyTransform();
    } else if (pointers.size === 1) {
      if (scale > 1.01) { tx = e.clientX - panBaseX; ty = e.clientY - panBaseY; clampPan(); applyTransform(); }
      else { moved = e.clientX - downX; }
    }
  });

  function endPointer(e) {
    pointers.delete(e.pointerId);
    if (pointers.size >= 2) { seedPinch(); return; }      // 3→2: re-anchor pinch
    if (pointers.size === 1) { seedPan(two()[0]); startDist = 0; return; } // 2→1: re-anchor pan to survivor
    // pointers.size === 0 → finalise tap / swipe
    var quick = Date.now() - downTime < 250;
    var onBackdrop = (downTarget === stage || downTarget === dlg);
    if (scale <= 1.01 && Math.abs(moved) > 60) { moved < 0 ? next() : prev(); moved = 0; lastTap = 0; return; }
    if (Math.abs(moved) < 10 && quick) {
      if (onBackdrop) { close(); lastTap = 0; return; }
      var now = Date.now();
      if (now - lastTap < 300) { toggleZoom(e.clientX, e.clientY); lastTap = 0; announce(); }
      else lastTap = now;
    }
    moved = 0;
  }
  stage.addEventListener('pointerup', endPointer);
  stage.addEventListener('pointercancel', endPointer);

  function toggleZoom(cx, cy) {
    if (scale > 1.01) { reset(); return; }
    scale = 2.5;
    var r = stage.getBoundingClientRect();
    tx = -(cx - (r.left + r.width  / 2)) * (scale - 1);
    ty = -(cy - (r.top  + r.height / 2)) * (scale - 1);
    clampPan(); applyTransform();
  }

  dlg.addEventListener('click', function (e) { if (e.target === dlg) { scale > 1.01 ? reset() : close(); } });
  stage.style.touchAction = 'none';

  /* ── Triggers ────────────────────────────────────────────────────────── */
  // Desktop: the single main image is a keyboard-operable zoom trigger.
  var mainImg = document.getElementById('apdpMainImage');
  if (mainImg && !mainImg.dataset.zoomBound) {
    mainImg.dataset.zoomBound = '1';
    mainImg.classList.add('is-zoomable');
    mainImg.setAttribute('role', 'button');
    mainImg.setAttribute('tabindex', '0');
    mainImg.setAttribute('aria-label', (mainImg.getAttribute('alt') || 'Product image') + ' — open larger view');
    ['pointerenter', 'focus', 'pointerdown'].forEach(function (eventName) {
      mainImg.addEventListener(eventName, function () {
        warmZoomImage(activeIndex(), eventName === 'pointerdown' ? 'high' : 'low');
      }, { passive: true });
    });
    mainImg.addEventListener('click', function () { open(activeIndex()); });
    mainImg.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(activeIndex()); }
    });
  }
  // Mobile: ONE delegated tap handler on the carousel — no per-slide focusable
  // controls (avoids N duplicate Tab stops over the existing thumbnail tablist).
  var slidesWrap = root.querySelector('#gallerySlides');
  if (slidesWrap) {
    slidesWrap.classList.add('gallery-slides--zoomable');
    slidesWrap.addEventListener('click', function (e) {
      var slide = e.target.closest && e.target.closest('.gallery-slide');
      if (!slide) return;
      var list = slidesWrap.querySelectorAll('.gallery-slide');
      var idx = Array.prototype.indexOf.call(list, slide);
      if (idx >= 0) open(idx);
    });
  }
  function activeIndex() {
    var active = root.querySelector('.gallery-thumb.active');
    if (!active) return 0;
    var i = Array.prototype.indexOf.call(root.querySelectorAll('.gallery-thumb'), active);
    return i < 0 ? 0 : i;
  }
})();
